import { useState } from 'react';
import { useMemoizedFn, useDebounceFn } from 'ahooks';
import { Drawer, message, Space } from 'antd';
import { Tabs } from 'antd-v5';
import { CloseOutlined, ExpandOutlined } from '@ant-design/icons';
import api from '@/api';
import type {
  ListDiagramByCodeUsingPostApiResponse as RelatedCircuitDiagrams,
  Diagram
} from '@/api/sip/bookmap/listDiagramByCodeUsingPOST';
import { CircuitDiagramType } from './constants';
import Main from './Main';
import { useTranslation } from '@/hooks/useTranslation';

import styles from './index.module.less';
type DetailProps = {
  topicCode: string;
  topicVersion: string;
  bookmapCode: string;
  bookmapVersion: string;
  defaultPCode: string;
  paramMaps?: Record<string, string>; // 参数定义列表
};

export default (props: DetailProps) => {
  const $ = useTranslation();
  const { bookmapCode, bookmapVersion } = props;
  const [links, setLinks] = useState<RelatedCircuitDiagrams[]>([]); // 可能有多个drawer嵌套
  const [isLargeDrawer, setIsLargeDrawer] = useState<boolean>(false);

  /**
   * @desc 点击电路图里的pode，新开Drawer展示与其关联的系列文章
   */
  const { run: openDrawerWithCodeAndPCode } = useDebounceFn(
    async ({ topicCode, topicVersion, pcode }) => {
      let res: any = {};
      const params = {
        topicCode,
        topicVersion,
        pcode,
        bookmapCode,
        bookmapVersion
      };
      res = await api.sip.bookmap.listDiagrams(params);
      if (res.code === 200) {
        if (Array.isArray(res.data) && res.data.length)
          setLinks(plinks => {
            plinks.push(res.data);
            return [...plinks];
          });
      } else message.error(res.msg);
    },
    {
      wait: 500,
      leading: true,
      trailing: false
    }
  );

  /**
   * @desc 点击drawer里的电路图title，新开Drawer展示该文章的详情
   */
  const openDrawerWithDiagramAndPCode = useMemoizedFn(
    ({ diagram, pcode, type }: { diagram: Diagram; pcode: string; type: string }) => {
      setLinks(plinks => {
        plinks.push([{ type, diagrams: [diagram], pcode }]);
        return [...plinks];
      });
    }
  );

  const handleClose = useMemoizedFn(index => {
    const filteredLinks = links.filter((_, i) => i !== index);
    setLinks(filteredLinks);
    if (!filteredLinks.length) {
      setIsLargeDrawer(false);
    }
  });

  const handleResize = useMemoizedFn(() => {
    setIsLargeDrawer(preState => !preState);
  });

  const TabBarExtraContent = ({ index }) => {
    return (
      <Space size={16}>
        <ExpandOutlined onClick={handleResize} />
        <CloseOutlined onClick={() => handleClose(index)} />
      </Space>
    );
  };

  const generateTabItems = useMemoizedFn(link =>
    link.map(item => ({
      key: item.type,
      label: $(CircuitDiagramType[item.type]),
      children:
        Array.isArray(item.diagrams) && item.diagrams?.length > 1 ? (
          item.diagrams?.map(diagram => (
            <div
              className={styles.text}
              onClick={() =>
                openDrawerWithDiagramAndPCode({
                  diagram,
                  pcode: item.pcode,
                  type: item.type
                })
              }
              key={diagram.topicCode}
            >
              {diagram.name}
            </div>
          ))
        ) : (
          <Main
            {...props}
            topicCode={item.diagrams[0].topicCode}
            topicVersion={item.diagrams[0].topicVersion}
            defaultPCode={item.pcode}
            openDrawer={openDrawerWithCodeAndPCode}
          />
        )
    }))
  );

  return (
    <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
      <Main {...props} openDrawer={openDrawerWithCodeAndPCode} />
      {links.map((link, index) => (
        <Drawer
          className={styles.rightDrawer}
          key={`${index + 1}`}
          placement='right'
          visible={true}
          onClose={() => handleClose(index)}
          getContainer={false}
          width={isLargeDrawer ? '90%' : '50%'}
        >
          <Tabs
            destroyInactiveTabPane={true}
            items={generateTabItems(link)}
            tabBarExtraContent={<TabBarExtraContent index={index} />}
          />
        </Drawer>
      ))}
    </div>
  );
};
